import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { NodeDataApi } from "~/api/node-data-api";
import { ContentContainer } from "~/components/common/content-container";
import { Dialog } from "~/components/common/Dialog";
import { Button, Input } from "~/components/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { TagDataCreateDto } from "~/types/api";

export default function SensorValueFormField({
  value,
  options,
}: {
  value?: string;
  options?: { label: string; value: string }[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<boolean>(false);

  async function handleSave() {
    setDialog(false);
    queryClient.invalidateQueries({ queryKey: ["currents"] });
  }

  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            aria-label="Add sensor value"
            onClick={() => setDialog(true)}
            type="button"
          >
            <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 " />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("common.new")}</TooltipContent>
      </Tooltip>
      <Dialog
        title={t("tagKey.createValue")}
        description={t("tagKey.createValueDescription")}
        width="40vw"
        maxWidth="40vw"
        open={dialog}
        onOpenChange={setDialog}
        externalTrigger={true}
        children={
          <SensorValueInput
            deveui={options?.find((x) => x.label == "deveui")?.value ?? ""}
            sensorKey={options?.find((x) => x.label == "key")?.value ?? ""}
            onSave={handleSave}
            onCancel={() => setDialog(false)}
          />
        }
      />
    </div>
  );
}

function SensorValueInput({
  deveui,
  sensorKey,
  onSave,
  onCancel,
}: {
  deveui: string;
  sensorKey: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState<string>("");

  async function handleCreate() {
    const result = await new NodeDataApi().createTagData(deveui, {
      key: sensorKey,
      value: localValue,
    } as TagDataCreateDto);
    if (!result.ok) {
      toast.error(t("common.errors.createFailed"));
      console.error(result.error);
      return;
    }
    toast.success(t("common.created", { resource: t("tagKey.fields.value") }));
    onSave(localValue);
  }

  return (
    <ContentContainer>
      <div>
        <div className="mr-2">
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
          />
        </div>
        <div className="mt-2">
          <Button
            className="mr-1"
            aria-label="create sensor value"
            onClick={handleCreate}
            type="button"
          >
            {t("common.save")}
          </Button>
          <Button
            variant="outline"
            aria-label="close"
            onClick={onCancel}
            type="button"
          >
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </ContentContainer>
  );
}
